import { useState } from "react";
import { toast } from "sonner";
import { Smartphone, Share, SquarePlus, CheckCircle2, EllipsisVertical, MonitorDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useLang } from "@/context/LangContext";
import { useInstall, isIos } from "@/lib/install";

const Step = ({ n, Icon, text }) => (
  <li className="flex items-center gap-4 rounded-sm bg-[#F2ECE1] p-4" data-testid={`install-step-${n}`}>
    <span className="grid place-items-center w-8 h-8 shrink-0 rounded-full bg-[#1C1917] text-[#FAF7F2] text-sm font-bold">{n}</span>
    <span className="grid place-items-center w-12 h-12 shrink-0 rounded-xl bg-white border border-black/10 text-[#0A84FF]"><Icon className="w-6 h-6" /></span>
    <span className="text-sm font-semibold leading-relaxed">{text}</span>
  </li>
);

const HowTo = ({ open, onOpenChange }) => {
  const { t, isRtl } = useLang();
  const s = t.install;
  const ios = isIos();
  const steps = ios
    ? [[Share, s.ios1], [SquarePlus, s.ios2], [CheckCircle2, s.ios3]]
    : [[EllipsisVertical, s.other1], [MonitorDown, s.other2], [CheckCircle2, s.other3]];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir={isRtl ? "rtl" : "ltr"} className="max-w-md" data-testid="install-dialog">
        <DialogHeader className="text-start">
          <div className="flex items-center gap-3 mb-2">
            <img src="/icon-192.png" alt="" className="w-14 h-14 rounded-2xl shadow-md" />
            <DialogTitle className="font-display text-2xl">{s.title}</DialogTitle>
          </div>
          <DialogDescription>{ios ? s.iosSub : s.otherSub}</DialogDescription>
        </DialogHeader>
        <ol className="space-y-3" data-testid={ios ? "install-steps-ios" : "install-steps-other"}>
          {steps.map(([Icon, text], i) => <Step key={text} n={i + 1} Icon={Icon} text={text} />)}
        </ol>
      </DialogContent>
    </Dialog>
  );
};

// Adds the site to the home screen (phone) or as an app (computer). Hidden once it is opened as an app.
export const InstallButton = ({ className = "" }) => {
  const { t } = useLang();
  const { installed, canPrompt, prompt } = useInstall();
  const [help, setHelp] = useState(false);
  if (installed) return null;
  const click = async () => {
    if (canPrompt) {
      if (await prompt()) toast.success(t.install.done);
      return;
    }
    setHelp(true);
  };

  return (
    <>
      <button onClick={click} data-testid="install-app-btn"
        className={`group relative inline-flex items-center justify-center gap-2 rounded-full bg-[#FBBF24] text-[#1C1917] whitespace-normal text-center leading-snug px-4 lg:px-6 py-2.5 lg:py-3 font-bold shadow-[0_6px_20px_-6px_rgba(217,119,6,0.7)] border-2 border-[#F59E0B] hover:bg-[#F59E0B] hover:-translate-y-0.5 transition-[background-color,transform] ${className}`}>
        <span className="absolute -top-1 -end-1 flex w-3.5 h-3.5" aria-hidden="true">
          <span className="absolute inset-0 rounded-full bg-[#047857] opacity-75 animate-ping" />
          <span className="relative w-3.5 h-3.5 rounded-full bg-[#047857] border-2 border-white" />
        </span>
        <Smartphone className="w-5 h-5 transition-transform group-hover:rotate-12" /> {t.install.btn}
      </button>
      <HowTo open={help} onOpenChange={setHelp} />
    </>
  );
};
